import { useMFContext } from 'components/MFHelpers'
import styled from 'styled-components'
import { SwissMap } from 'components/ChoroplethMap'
import ColorKey from 'components/ColorKey'
import { useEffect, useMemo, useState } from 'react'
import ToolTip from 'components/ToolTip'
import { scaleLinear } from 'd3'
import { jenks } from 'simple-statistics'
import MunicipalitySearch from '@ta-interaktiv/react-municipality-search'
import Chart from 'components/Chart'
import DataTable from 'components/DataTable'
import ColorKeyGradient from 'components/ColorKeyGradient'
import { Column } from 'components/DataTable/DataTable'

const GDEMap = () => {
  const { currentLanguage, isMobile, colorMode, _t } = useMFContext()
  const [currentGdeNr, setcurrentGdeNr] = useState<number>(0)
  const [currentGdeData, setCurrentGdeData] = useState<any>(null)
  const [minMax, setMinMax] = useState<{ min: number; max: number }>({
    min: 0,
    max: 100,
  })
  const [gdedata, setGdedata] = useState<any>(null)
  const columns: Column[] = [
    {
      key: 'Gemeinde',
      label: _t('muni') as string,
      align: 'left',
      width: isMobile ? '160px' : '240px',
      sortable: true,
    },
    {
      key: 'Kanton',
      label: _t('cantonAbbr') as string,
      align: 'left',
      width: '40px',
      sortable: true,
    },

    {
      key: 'TotalPotentialProductionGWh',
      label: (
        <>
          {_t('gde_potential')}
          <br />
          <Unit>{_t('inGwh')}</Unit>
        </>
      ),
      align: 'right',
      width: '80px',
      format: (e: number) => e.toLocaleString('de-CH').replace('.', ','),
      sortable: true,
    },
    {
      key: 'CumEffectivePotentialUsed',
      label: (
        <>
          {_t('gde_usage')}
          <br />
          <Unit>{_t('inPercent')}</Unit>
        </>
      ),
      valuesAsBars: true,
      barColor: 'var(--yellow-main)',
      barMax: 65,
      align: 'left',
      format: (e: number) => e.toLocaleString('de-CH').replace('.', ','),
      sortable: true,
    },
  ]
  const columnsDesktopOnly: Column[] = [
    {
      key: 'CumNumberPlants',
      label: _t('anlagen') as string,
      align: 'right',
      width: '80px',
      sortable: true,
    },
    {
      key: 'CumTotalProductionGWh',
      label: (
        <>
          {_t('production')}
          <br />
          <Unit>{_t('inGwh')}</Unit>
        </>
      ),
      align: 'right',
      width: '100px',
      sortable: true,
    },
  ]

  // fetch data from url
  useEffect(() => {
    console.log('fetching data')
    fetch(
      'https://interaktiv.tagesanzeiger.ch/2025/solar/gdedata_' +
        currentLanguage +
        '.json'
    )
      .then((response) => response.json())
      .then((data) => {
        console.log('loaded', data)
        setGdedata(data)
      })
  }, [currentLanguage])

  useEffect(() => {
    var min = 0
    var max = 100
    if (gdedata) {
      min = Math.min(...gdedata.map((m: any) => m.CumEffectivePotentialUsed))
      max = Math.max(...gdedata.map((m: any) => m.CumEffectivePotentialUsed))
    }
    setMinMax({ min, max })
  }, [gdedata])

  useEffect(() => {
    if (currentGdeNr) {
      var gdedata_filtered = gdedata.filter(
        (m: any) => m.bfsId === currentGdeNr
      )
      setCurrentGdeData(gdedata_filtered[0])
    } else {
      setCurrentGdeData(null)
    }
  }, [currentGdeNr, gdedata])

  const yellowOrange2 = [
    '#ffffd4',
    '#fff1b1',
    '#ffe462',
    '#ecb932',
    '#e0961a',
    '#E16D00',
  ]

  const jenksBreaks = useMemo(() => {
    if (!gdedata) return [1]
    return jenks(
      [0.0000001, ...gdedata.map((m: any) => m.CumEffectivePotentialUsed)],
      7
    )
  }, [gdedata])

  let color_linear = useMemo(() => {
    if (!jenksBreaks) return () => '#000000'
    return scaleLinear<string>()
      .domain(jenksBreaks)
      .range(yellowOrange2)
      .clamp(true)
  }, [minMax, jenksBreaks])

  const colorMunicipality = useMemo(
    () => (e: any) => {
      return color_linear(e.CumEffectivePotentialUsed)
    },
    [gdedata, color_linear]
  )
  if (!gdedata) return null

  const setSelectedMunicipality = (municipality: any) => {
    setcurrentGdeNr(municipality.GDENR)
  }
  return (
    <>
      <Chart
        innerSpace={0}
        meta={{
          title: _t('gdemap_title'),
          subtitle: _t('gdemap_subtitle'),
        }}
        spaceAfter={0}
        addToHead={
          <>
            <ColorKeyContainer>
              <ColorKeyGradient
                height={8}
                data={jenksBreaks.map((value) => ({
                  color: color_linear(value),
                  value: value,
                }))}
                labels={[
                  { value: jenksBreaks[0], label: Math.round(jenksBreaks[0]) },
                  {
                    value: jenksBreaks[jenksBreaks.length - 1],
                    label:
                      Math.round(jenksBreaks[jenksBreaks.length - 1]) + '%',
                  },
                ]}
              />
            </ColorKeyContainer>{' '}
            <SearchContainer>
              <MunicipalitySearch
                placeholder={_t('gdemap_search_placeholder') as string}
                locale={currentLanguage}
                resetOnSelect
                municipalityData={
                  currentLanguage == 'de'
                    ? '20250101_de_nozip'
                    : '20250101_fr_nozip'
                }
                onSelectionHandler={setSelectedMunicipality}
              />
            </SearchContainer>
          </>
        }
      >
        <>
          <SwissMap
            colorMode={colorMode}
            data={gdedata.map((m: any) => {
              return {
                bfsId: m.gde,
                ...m,
              }
            })}
            onBfsIdChange={(newBfsId?: number) => {
              setcurrentGdeNr(newBfsId)
            }}
            colorMunicipality={colorMunicipality}
            LegendComponent={ColorKey}
            setActiveMunicipalityId={(newBfsId?: number) => {
              setcurrentGdeNr(newBfsId)
            }}
            activeMunicipalityId={currentGdeNr}
            topoJsonYear={2024}
            isZoomable={true}
            topoJson={require('../ChoroplethMap/Assets/temp.json')}
            ToolTip={ToolTip}
            layerStyles={{
              // country: {
              //   stroke:
              //     colorMode === 'dark' ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.3)', // 'var(--gray-3)',
              //   strokeWidth: '1px',
              //   fill: 'none',
              // },
              unproductive: {
                fill: 'url(#pattern-circles)',
                strokeWidth: '.5px',
                stroke: 'none',
              },
              municipality: {
                strokeWidth: '0.5px',
                stroke: 'rgba(0,0,0,.2)',
                fill: 'var(--gray-1)',
              },
              municipalityHover: {
                strokeWidth: '0.25px',
                stroke: 'var(--gray-4)',
              },
              lakes: {
                fill:
                  colorMode === 'dark'
                    ? 'var(--brandblue-main)'
                    : 'var(--brandblue-bright-4)',
                strokeWidth: '0.5px',
                stroke: 'rgba(0,0,0,.2)',
              },
            }}
          />

          <ActiveMuniDisplayContainer
            style={{
              maxHeight: currentGdeData ? '300px' : '0px',
            }}
          >
            {currentGdeData && (
              <>
                <ToolTip
                  activeData={currentGdeData}
                  currentName={currentGdeData.Gemeinde}
                  propertyName='CumEffectivePotentialUsed'
                  isClosable={true}
                  hasShadow={true}
                  closePressed={() => {}}
                />
                <CloseButton
                  onClick={() => {
                    // setSelectedMunicipality(null)
                    setcurrentGdeNr(null)
                  }}
                >
                  X
                </CloseButton>
              </>
            )}
          </ActiveMuniDisplayContainer>
        </>
      </Chart>
      <Chart
        meta={{
          source: (
            <>
              {_t('source')}:{' '}
              <a href='https://www.uvek-gis.admin.ch/BFE/storymaps/EE_Elektrizitaetsproduktionsanlagen/'>
                {_t('linechart_source')}
              </a>
            </>
          ),
        }}
        spaceBefore={0}
        contentWidth='TextWidthExtendedFullMobile'
      >
        <DataTable
          hideSearch
          selected={
            currentGdeData ? { prop: 'bfsId', value: currentGdeNr } : undefined
          }
          data={gdedata}
          columns={[...columns, ...(isMobile ? [] : columnsDesktopOnly)]}
          defaultSort={{
            key: 'CumEffectivePotentialUsed',
            direction: 'desc',
          }}
          defaultRowCount={15}
        />
      </Chart>
    </>
  )
}

const ActiveMuniDisplayContainer = styled.div`
  margin-top: -100px;
  margin-bottom: 0px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1012;
  max-width: 400px;

  max-height: 0;
  overflow: hidden;
  transition: max-height 0.6s ease-in-out, margin-top 0.6s ease-in-out;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
  border-radius: 10px;
`

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: var(--beige-bright-1) !important;
  border: 0;
  padding: 5px 10px;
  color: var(--beige-dark-2);
`

export default GDEMap

const ColorKeyContainer = styled.div`
  max-width: 400px;
`

const SearchContainer = styled.div`
  position: relative;
  z-index: 1012;
  max-width: 400px;
  margin-bottom: 15px;
  .municipality-search {
    font-size: 16px;
  }
`

const Unit = styled.span`
  font-size: 12px;
  font-weight: 400;
  /* color: var(--gray-4); */
`
